import {useLocalStorage} from 'platform/components';

import {useEffect, useReducer, useRef, useState} from 'react';

import {isEmpty, isNotNil} from 'ramda';

import {Nullish, useQueryState} from 'shared/web';

import {AUCTION_DEFAULT_ORDER_ID} from '../../../constants/AUCTION_DEFAULT_ORDER_ID';
import {CLASSIFIEDS_DEFAULT_ORDER_ID} from '../../../constants/CLASSIFIEDS_DEFAULT_ORDER_ID';
import {useSourcingCurrency} from '../../../hooks/useSourcingCurrency';
import {useSourcingTargetCountry} from '../../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {useFilters} from '../../../pages/AllOffers/components/FiltersContext/FiltersContext';
import {reducer, SourcingTableDispatch} from '../../../store/sourcingTable/reducer';
import {SourcingTableName} from '../types/SourcingTableName';
import {SourcingTableState} from '../types/SourcingTableState';
import {sourcingTableSessionStorage} from '../utils/sourcingTableSessionStorage';

export function useSourcingTable(
  tableName: SourcingTableName,
  initialOrderBy: string,
  appliedFilterPresetId?: string | Nullish
): [SourcingTableState, SourcingTableDispatch] {
  const [queryFilterPresetId] = useQueryState('filterPresetId');
  const [orderByFromLocalStorage, saveOrderByToLocalStorage] = useLocalStorage(
    `sourcing-table--sorting/${tableName}`,
    initialOrderBy
  );
  const sourcingType = useSourcingType();

  const [state, dispatch] = useReducer(reducer, {
    tableName,
    orderBy: isEmpty(orderByFromLocalStorage) ? initialOrderBy : orderByFromLocalStorage,
    page: 1,
    checkedRows: [],
    selectedRow: null,
    expandedRowIds: [],
    isRealTime: sourcingTableSessionStorage.getIsRealTime(),
  });

  const {filters} = useFilters();
  const currency = useSourcingCurrency();
  const targetCountry = useSourcingTargetCountry();

  // Render-time page reset: dispatch during render so consumers reading state.page see 1 in the
  // same render as the input change, avoiding the one-render race that a useEffect would create.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevQueryInputs, setPrevQueryInputs] = useState({
    filters,
    currencyCode: currency?.code,
    countryCode: targetCountry?.code,
    appliedFilterPresetId,
  });
  const queryInputsChanged =
    prevQueryInputs.filters !== filters ||
    prevQueryInputs.currencyCode !== currency?.code ||
    prevQueryInputs.countryCode !== targetCountry?.code ||
    prevQueryInputs.appliedFilterPresetId !== appliedFilterPresetId;

  if (queryInputsChanged) {
    setPrevQueryInputs({
      filters,
      currencyCode: currency?.code,
      countryCode: targetCountry?.code,
      appliedFilterPresetId,
    });
    if (state.page !== 1) {
      dispatch({type: 'changePage', page: 1});
    }
  }

  // Query parameter is added when clicking on notification; handled in useSourcingFilterPresets()
  useEffect(
    function resetSortOnNotificationPreset() {
      if (isNotNil(queryFilterPresetId)) {
        dispatch({
          type: 'sort',
          column:
            sourcingType === SourcingType.AUCTIONS
              ? AUCTION_DEFAULT_ORDER_ID
              : CLASSIFIEDS_DEFAULT_ORDER_ID,
        });
      }
    },
    [queryFilterPresetId, sourcingType]
  );

  useEffect(
    function persistOrderBy() {
      saveOrderByToLocalStorage(state.orderBy);
    },
    [state.orderBy, saveOrderByToLocalStorage]
  );

  // Skip the first run so the initial `state.page === 1` does not overwrite the page that
  // `useSourcingTableSelectionEffects` is about to restore from session storage on first data
  // arrival.
  const hasMountedRef = useRef(false);
  useEffect(
    function persistLastVisitedPage() {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        return;
      }
      sourcingTableSessionStorage.setLastVisitedPage(tableName, state.page);
    },
    [state.page, tableName]
  );

  useEffect(
    function persistRealTimePreference() {
      sourcingTableSessionStorage.setIsRealTime(state.isRealTime);
    },
    [state.isRealTime]
  );

  return [state, dispatch];
}
